import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import ArticleCard from '@/components/base/ArticleCard';
import { useSeo } from '@/hooks/useSeo';
import { searchArticles } from '@/utils/article';
import { hotKeywords } from '@/mocks/categories';

const SORTS = [
  { key: 'relevance', label: '相关度' },
  { key: 'time', label: '发布时间' },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get('q') || '';
  const [input, setInput] = useState(keyword);
  const [sort, setSort] = useState('relevance');

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useSeo({
    title: keyword ? `搜索「${keyword}」-黑料网曝大瓜` : '搜索-黑料网曝大瓜',
    description: keyword
      ? `在黑料网曝大瓜中搜索「${keyword}」相关的明星八卦、娱乐爆料与新闻资讯。`
      : '搜索黑料网曝大瓜的海量明星八卦与娱乐爆料内容。',
    keywords: '娱乐爆料,明星八卦,搜索,黑料网曝大瓜',
  });

  const results = useMemo(() => {
    const list = searchArticles(keyword);
    if (sort === 'time') return [...list].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
    return list;
  }, [keyword, sort]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const kw = input.trim();
    setParams(kw ? { q: kw } : {});
  };

  const quickSearch = (kw: string) => {
    setParams({ q: kw });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="w-full max-w-[1280px] mx-auto px-4 md:px-8 pt-8">
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6">
          <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800">搜索结果</span>
        </nav>

        <div className="rounded-xl bg-background-100 border border-background-200 p-6 md:p-8">
          <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">搜索大瓜</h1>
          <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="flex-1 flex items-center bg-background-50 border border-background-200 rounded-md pl-4 pr-2 h-12 focus-within:border-primary-400 transition-colors">
              <i className="ri-search-line text-foreground-400 text-lg" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入明星名字、话题或关键词，例如：顶流密会"
                className="flex-1 bg-transparent outline-none text-sm text-foreground-800 placeholder:text-foreground-400 px-3"
              />
            </div>
            <button type="submit" className="px-6 h-12 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              搜一下
            </button>
          </form>

          {keyword && (
            <div className="mt-5 flex items-center flex-wrap gap-2">
              <span className="text-xs text-foreground-500">当前关键词：</span>
              <span className="text-xs px-3 py-1 rounded-full bg-primary-500 text-background-50">{keyword}</span>
              <Link to="/search" className="text-xs text-foreground-500 hover:text-primary-600 transition-colors">清除</Link>
            </div>
          )}
        </div>

        {keyword ? (
          <div className="pt-8">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
              <p className="text-sm text-foreground-500">
                共找到 <span className="text-primary-600 font-semibold">{results.length}</span> 条与「{keyword}」相关的结果
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-400">排序：</span>
                {SORTS.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setSort(s.key)}
                    className={`px-3 py-1.5 rounded-md text-xs transition-colors cursor-pointer ${sort === s.key ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:text-primary-600'}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {results.length > 0 ? (
              <div className="flex flex-col gap-3">
                {results.map((a) => (
                  <ArticleCard key={a.id} article={a} variant="list" />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-background-300 py-16 text-center">
                <i className="ri-search-eye-line text-5xl text-foreground-300" />
                <p className="mt-4 text-base text-foreground-800">没有找到与「{keyword}」相关的内容</p>
                <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或看看下面的热门搜索。</p>
                <div className="mt-6 flex items-center justify-center flex-wrap gap-2">
                  {hotKeywords.slice(0, 6).map((kw) => (
                    <button
                      key={kw}
                      type="button"
                      onClick={() => quickSearch(kw)}
                      className="px-3 py-1.5 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      {kw}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="pt-10">
            <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-fire-line text-secondary-600" /> 热门搜索
            </h2>
            <div className="flex flex-wrap gap-2">
              {hotKeywords.map((kw) => (
                <button
                  key={kw}
                  type="button"
                  onClick={() => quickSearch(kw)}
                  className="px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  {kw}
                </button>
              ))}
            </div>
            <p className="mt-8 text-xs text-foreground-400">
              提示：搜索为前端演示功能，基于站内示例内容进行匹配。
            </p>
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}